<script setup lang="ts">
import { TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref, watch } from 'vue'

import { STATUS_LABELS as ASN_STATUS } from '../api/inbound'
import { STATUS_LABELS as ORDER_STATUS } from '../api/outbound'
import {
  DOC_TYPE_LABELS,
  exportDocuments,
  exportMoves,
  exportStockSummary,
  getReportDocuments,
  getReportMoves,
  getStockSummary,
  type DocRow,
  type DocType,
  type SummaryRow,
} from '../api/reports'
import { MOVE_LABELS, type MoveRow } from '../api/stock'
import { STATUS_LABELS as TAKE_STATUS } from '../api/stocktakes'
import { listWarehouses, type Warehouse } from '../api/warehouses'
import { STATUS_LABELS as WAVE_STATUS } from '../api/waves'
import { csvName, saveBlob } from '../utils/download'

type TabKey = 'summary' | 'moves' | 'docs'

const tab = ref<TabKey>('summary')
const error = ref<unknown>(null)
const notice = ref('')
const loading = ref(false)
const exporting = ref(false)
const warehouses = ref<Warehouse[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const summary = reactive({
  query: { keyword: '', warehouse: undefined as number | undefined, page: 1, size: 20 },
  rows: [] as SummaryRow[],
  total: 0,
})

const moves = reactive({
  query: {
    keyword: '',
    warehouse: undefined as number | undefined,
    move_type: '',
    from: '',
    to: '',
    page: 1,
    size: 20,
  },
  dates: [] as string[],
  rows: [] as MoveRow[],
  total: 0,
})

const docs = reactive({
  query: {
    type: '' as DocType | '',
    keyword: '',
    warehouse: undefined as number | undefined,
    status: '',
    page: 1,
    size: 20,
  },
  rows: [] as DocRow[],
  total: 0,
})

function label(id: number, list: { id: number; code: string; name: string }[]): string {
  const hit = list.find((item) => item.id === id)
  return hit ? `${hit.code} ${hit.name}`.trim() : `#${id}`
}

async function loadSummary(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await getStockSummary({
      page: summary.query.page,
      size: summary.query.size,
      keyword: summary.query.keyword.trim() || undefined,
      warehouse: summary.query.warehouse || undefined,
    })
    summary.rows = res.list
    summary.total = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    summary.rows = []
  } finally {
    loading.value = false
  }
}

async function loadMoves(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await getReportMoves({
      page: moves.query.page,
      size: moves.query.size,
      keyword: moves.query.keyword.trim() || undefined,
      warehouse: moves.query.warehouse || undefined,
      move_type: moves.query.move_type || undefined,
      from: moves.dates[0] || undefined,
      to: moves.dates[1] || undefined,
    })
    moves.rows = res.list
    moves.total = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    moves.rows = []
  } finally {
    loading.value = false
  }
}

async function loadDocs(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await getReportDocuments({
      page: docs.query.page,
      size: docs.query.size,
      type: docs.query.type || undefined,
      keyword: docs.query.keyword.trim() || undefined,
      warehouse: docs.query.warehouse || undefined,
      status: docs.query.status || undefined,
    })
    docs.rows = res.list
    docs.total = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    docs.rows = []
  } finally {
    loading.value = false
  }
}

const loaders: Record<TabKey, (notice?: string) => Promise<void>> = {
  summary: loadSummary,
  moves: loadMoves,
  docs: loadDocs,
}

onMounted(() => {
  void loadSummary()
  void (async () => {
    refError.value = null
    try {
      warehouses.value = (await listWarehouses({ page: 1, size: 100 })).list
    } catch (e) {
      // 参照数据拿不到下拉会空，但必须把原因露出来（如未选货主时后端回 422），否则页面看着像没数据
      refError.value = e
    }
  })()
})

function changeTab(key: TabKey | string): void {
  tab.value = key as TabKey
  void loaders[tab.value]()
}

async function runExport(run: () => Promise<Blob>, filename: string): Promise<void> {
  exporting.value = true
  error.value = null
  try {
    saveBlob(await run(), csvName(filename))
    notice.value = 'CSV 已开始下载'
  } catch (e) {
    error.value = e
  } finally {
    exporting.value = false
  }
}

const exportCurrent = (): Promise<void> => {
  if (tab.value === 'summary') {
    return runExport(
      () =>
        exportStockSummary({
          keyword: summary.query.keyword.trim() || undefined,
          warehouse: summary.query.warehouse || undefined,
        }),
      'stock-summary',
    )
  }
  if (tab.value === 'moves') {
    return runExport(
      () =>
        exportMoves({
          keyword: moves.query.keyword.trim() || undefined,
          warehouse: moves.query.warehouse || undefined,
          move_type: moves.query.move_type || undefined,
          from: moves.dates[0] || undefined,
          to: moves.dates[1] || undefined,
        }),
      'stock-moves',
    )
  }
  return runExport(
    () =>
      exportDocuments({
        type: docs.query.type || undefined,
        keyword: docs.query.keyword.trim() || undefined,
        warehouse: docs.query.warehouse || undefined,
        status: docs.query.status || undefined,
      }),
    'documents',
  )
}

/** 本页合计：汇总页数量是跨仓库的，看一眼本页总量就知道大概规模 */
const summaryTotals = computed(() => {
  const acc = { qty: 0, locked: 0, available: 0 }
  for (const row of summary.rows) {
    acc.qty += row.qty
    acc.locked += row.locked_qty
    acc.available += row.available
  }
  return acc
})

/** 单据类型选定后才给状态选项：状态字典每种单据各一套 */
const statusOptions = computed(() => {
  const maps: Record<DocType, Record<string, string>> = {
    asn: ASN_STATUS,
    order: ORDER_STATUS,
    wave: WAVE_STATUS,
    stocktake: TAKE_STATUS,
  }
  return docs.query.type ? maps[docs.query.type] : {}
})

watch(
  () => docs.query.type,
  () => {
    docs.query.status = ''
    docs.query.page = 1
    void loadDocs()
  },
)

const moveTypeOptions = Object.entries(MOVE_LABELS)
</script>

<template>
  <div class="yc-report">
    <el-tabs v-model="tab" class="yc-report__tabs" @tab-change="changeTab">
      <el-tab-pane label="库存汇总" name="summary" />
      <el-tab-pane label="出入库流水" name="moves" />
      <el-tab-pane label="单据导出" name="docs" />
    </el-tabs>

    <header class="yc-report__bar">
      <!-- 库存汇总筛选 -->
      <template v-if="tab === 'summary'">
        <el-input
          v-model="summary.query.keyword"
          class="yc-report__keyword"
          placeholder="商品编码/名称/批次"
          clearable
          @keyup.enter="loadSummary()"
          @clear="loadSummary()"
        />
        <el-select
          v-model="summary.query.warehouse"
          class="yc-report__ref"
          placeholder="全部仓库"
          clearable
          @change="loadSummary()"
        >
          <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
        </el-select>
        <el-button @click="loadSummary()">查询</el-button>
      </template>

      <!-- 流水筛选 -->
      <template v-else-if="tab === 'moves'">
        <el-input
          v-model="moves.query.keyword"
          class="yc-report__keyword"
          placeholder="单据号/商品"
          clearable
          @keyup.enter="loadMoves()"
          @clear="loadMoves()"
        />
        <el-select
          v-model="moves.query.move_type"
          class="yc-report__type"
          placeholder="全部类型"
          clearable
          @change="loadMoves()"
        >
          <el-option v-for="[value, text] in moveTypeOptions" :key="value" :label="text" :value="value" />
        </el-select>
        <el-select
          v-model="moves.query.warehouse"
          class="yc-report__ref"
          placeholder="全部仓库"
          clearable
          @change="loadMoves()"
        >
          <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
        </el-select>
        <el-date-picker
          v-model="moves.dates"
          class="yc-report__dates"
          type="daterange"
          value-format="YYYY-MM-DD"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          @change="loadMoves()"
        />
        <el-button @click="loadMoves()">查询</el-button>
      </template>

      <!-- 单据导出筛选 -->
      <template v-else>
        <el-select
          v-model="docs.query.type"
          class="yc-report__doctype"
          placeholder="全部单据"
          clearable
        >
          <el-option v-for="(text, value) in DOC_TYPE_LABELS" :key="value" :label="text" :value="value" />
        </el-select>
        <el-select
          v-if="docs.query.type"
          v-model="docs.query.status"
          class="yc-report__status"
          placeholder="全部状态"
          clearable
          @change="loadDocs()"
        >
          <el-option v-for="(text, value) in statusOptions" :key="value" :label="text" :value="value" />
        </el-select>
        <el-input
          v-model="docs.query.keyword"
          class="yc-report__keyword"
          placeholder="单号"
          clearable
          @keyup.enter="loadDocs()"
          @clear="loadDocs()"
        />
        <el-select
          v-model="docs.query.warehouse"
          class="yc-report__ref"
          placeholder="全部仓库"
          clearable
          @change="loadDocs()"
        >
          <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
        </el-select>
        <el-button @click="loadDocs()">查询</el-button>
      </template>

      <el-button @click="loaders[tab]()">刷新</el-button>
      <el-button
        v-perm="'wms:report:export'"
        :loading="exporting"
        type="primary"
        @click="exportCurrent"
      >
        导出 CSV
      </el-button>
      <span v-if="notice" class="yc-report__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-report__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <!-- 库存汇总 -->
    <el-card v-if="tab === 'summary'" v-loading="loading" class="yc-report__card">
      <el-table :data="summary.rows" row-key="id">
        <el-table-column prop="warehouse_name" label="仓库" min-width="130" />
        <el-table-column prop="sku_code" label="商品编码" width="130" />
        <el-table-column prop="sku_name" label="商品名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="unit" label="单位" width="70" />
        <el-table-column prop="batch_no" label="批次" width="110">
          <template #default="{ row }">{{ row.batch_no || '—' }}</template>
        </el-table-column>
        <el-table-column label="效期" width="110">
          <template #default="{ row }">{{ row.expiry_date ? row.expiry_date.slice(0, 10) : '—' }}</template>
        </el-table-column>
        <el-table-column prop="qty" label="数量" width="90" align="right" />
        <el-table-column prop="locked_qty" label="锁定" width="90" align="right" />
        <el-table-column prop="available" label="可用" width="90" align="right" />
      </el-table>
      <div class="yc-report__totals">
        本页合计：数量 {{ summaryTotals.qty }}，锁定 {{ summaryTotals.locked }}，可用
        {{ summaryTotals.available }}（共 {{ summary.total }} 行）
      </div>
      <el-pagination
        v-if="summary.total > summary.query.size"
        class="yc-report__page"
        layout="total, prev, pager, next"
        :total="summary.total"
        :page-size="summary.query.size"
        :current-page="summary.query.page"
        @current-change="(p: number) => ((summary.query.page = p), loadSummary())"
      />
    </el-card>

    <!-- 出入库流水 -->
    <el-card v-else-if="tab === 'moves'" v-loading="loading" class="yc-report__card">
      <el-table :data="moves.rows" row-key="id">
        <el-table-column label="时间" width="160">
          <template #default="{ row }">{{ formatDateTime(row.moved_at) }}</template>
        </el-table-column>
        <el-table-column label="类型" width="100">
          <template #default="{ row }">{{ MOVE_LABELS[row.move_type] ?? row.move_type }}</template>
        </el-table-column>
        <el-table-column prop="warehouse_name" label="仓库" min-width="120" />
        <el-table-column label="源库位" width="100">
          <template #default="{ row }">{{ row.from_location_code || '—' }}</template>
        </el-table-column>
        <el-table-column label="目标库位" width="100">
          <template #default="{ row }">{{ row.to_location_code || '—' }}</template>
        </el-table-column>
        <el-table-column prop="sku_code" label="商品编码" width="130" />
        <el-table-column prop="sku_name" label="商品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="batch_no" label="批次" width="110">
          <template #default="{ row }">{{ row.batch_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="qty_delta" label="变动" width="90" align="right">
          <template #default="{ row }">
            <span :class="row.qty_delta < 0 ? 'yc-report__neg' : ''">{{ row.qty_delta }}</span>
          </template>
        </el-table-column>
        <el-table-column label="来源单据" min-width="170">
          <template #default="{ row }">{{ row.ref_no || row.ref_type || '—' }}</template>
        </el-table-column>
        <el-table-column prop="operator" label="操作人" width="110" />
      </el-table>
      <el-pagination
        v-if="moves.total > moves.query.size"
        class="yc-report__page"
        layout="total, prev, pager, next"
        :total="moves.total"
        :page-size="moves.query.size"
        :current-page="moves.query.page"
        @current-change="(p: number) => ((moves.query.page = p), loadMoves())"
      />
    </el-card>

    <!-- 单据导出 -->
    <el-card v-else v-loading="loading" class="yc-report__card">
      <el-table :data="docs.rows" row-key="id">
        <el-table-column label="单据类型" width="100">
          <template #default="{ row }">{{ row.type_label }}</template>
        </el-table-column>
        <el-table-column prop="doc_no" label="单号" width="180" />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">{{ row.status_label }}</template>
        </el-table-column>
        <el-table-column prop="warehouse_name" label="仓库" min-width="130" />
        <el-table-column prop="item_count" label="行数" width="80" align="right" />
        <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
        <el-table-column prop="created_by" label="创建人" width="110" />
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="docs.total > docs.query.size"
        class="yc-report__page"
        layout="total, prev, pager, next"
        :total="docs.total"
        :page-size="docs.query.size"
        :current-page="docs.query.page"
        @current-change="(p: number) => ((docs.query.page = p), loadDocs())"
      />
    </el-card>
  </div>
</template>

<style scoped lang="scss">
.yc-report {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__ref {
    width: 190px;
  }

  &__type,
  &__doctype {
    width: 140px;
  }

  &__status {
    width: 140px;
  }

  &__dates {
    width: 260px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__totals {
    margin-top: var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__neg {
    color: var(--yc-color-danger, #f56c6c);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
